import { useInView } from '@hooks/useScroll';
import { Ship, Wind, Users, Trophy, type LucideIcon } from 'lucide-react';
import { useSiteText } from '@/lib/SiteTextContext';
import { useSiteImages } from '@/lib/SiteImagesContext';
import type { ActivityCard } from '@/lib/store';

const ICONS: Record<string, LucideIcon> = {
  Sailboat: Ship,
  Wind: Wind,
  Users: Users,
  Trophy: Trophy,
};

export function SailingSection() {
  const { t } = useSiteText();
  const { activities } = useSiteImages();
  const { ref, isInView } = useInView();

  return (
    <section id="sailing" className="py-20 md:py-32 bg-gradient-to-b from-white to-foam-white overflow-hidden">
      <div className="container mx-auto px-6 md:px-12 lg:px-24">
        <div
          ref={ref}
          className={`text-center mb-16 transition-all duration-700 ease-sail ${
            isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
                    <p className="text-sm tracking-[0.3em] uppercase text-ocean-blue font-medium mb-4">
            {t('sailing.label')}
          </p>
          <h2 className="font-display text-4xl md:text-5xl font-bold text-ocean-blue mb-6 text-wave">
            {t('sailing.title')}
          </h2>
          <p className="text-text-muted font-light max-w-2xl mx-auto">
            {t('sailing.text')}
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {activities.map((act: ActivityCard, i) => {
            const Icon = ICONS[act.icon] || Ship;
            return (
              <div
                key={act.id}
                className={`group bg-white rounded-2xl overflow-hidden shadow-lg border border-gray-100 transition-all duration-700 ease-sail hover:shadow-2xl hover:-translate-y-2 hover:border-ocean-blue/20 ${
                  isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                }`}
                style={{ transitionDelay: `${i * 120}ms` }}
              >
                {act.image && (
                  <div className="h-40 overflow-hidden">
                    <img
                      src={act.image}
                      alt={act.title}
                      className="w-full h-full object-cover transition-transform duration-[1200ms] ease-sail group-hover:scale-110"
                    />
                  </div>
                )}
                <div className="p-6">
                  <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-ocean-blue/10 border border-ocean-blue/20 mb-4 transition-transform duration-500 ease-sail group-hover:scale-110 group-hover:rotate-6">
                    <Icon className="w-6 h-6 text-ocean-blue" />
                  </div>
                  <h3 className="font-display text-xl font-bold text-ocean-blue mb-2 transition-colors duration-300 group-hover:text-accent-gold-dark">{act.title}</h3>
                  <p className="text-sm text-text-muted font-light leading-relaxed">{act.description}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
